/**
 * @file CorsProxyRegistry.js
 * @description Módulo central para integrar la configuración CORS en la aplicación
 * @version 1.0.0
 * @author Nubelytics Engineering Team
 */

import axios from 'axios';
import corsProxyConfig, { addDomainToForceList, removeDomainFromForceList } from './CorsProxyConfig';
import CorsProxyInterceptorSimple from './CorsProxyInterceptorSimple';

/**
 * Clase para gestionar el registro de interceptores CORS en toda la aplicación
 * Implementa patrón Singleton para garantizar una única instancia
 */
class CorsProxyRegistry {
  constructor() {
    this.interceptors = new Map();
    this.diagnosticData = {
      proxiedRequests: 0,
      failedRequests: 0,
      lastError: null,
      domainsWithErrors: new Set()
    };
  }
  
  /**
   * Registra el interceptor CORS en una instancia de Axios
   * @param {Object} axiosInstance - Instancia de Axios a interceptar
   * @param {string} [instanceName='default'] - Nombre identificativo de la instancia
   * @returns {Object} Objeto con métodos para desregistrar
   */
  registerInterceptor(axiosInstance, instanceName = 'default') {
    if (!axiosInstance) {
      console.error('[CorsProxyRegistry] No se proporcionó una instancia válida de Axios');
      return null;
    }
    
    // Si ya existe un interceptor para esta instancia, desregistrarlo primero
    if (this.interceptors.has(instanceName)) {
      this.unregisterInterceptor(instanceName);
    }
    
    // Registrar nuevo interceptor
    const interceptor = CorsProxyInterceptorSimple.register(axiosInstance);
    this.interceptors.set(instanceName, {
      axiosInstance,
      interceptor,
      timestamp: Date.now()
    });
    
    console.info(`[CorsProxyRegistry] Interceptor registrado para instancia '${instanceName}'`);
    return interceptor;
  }
  
  /**
   * Desregistra un interceptor por su nombre
   * @param {string} instanceName - Nombre de la instancia
   * @returns {boolean} - true si se desregistró correctamente
   */
  unregisterInterceptor(instanceName) {
    if (!this.interceptors.has(instanceName)) {
      return false;
    }
    
    const { interceptor } = this.interceptors.get(instanceName);
    if (interceptor && interceptor.unregister) {
      interceptor.unregister();
      this.interceptors.delete(instanceName);
      console.info(`[CorsProxyRegistry] Interceptor desregistrado para instancia '${instanceName}'`);
      return true;
    }
    
    return false;
  }
  
  /**
   * Registra interceptores para todas las instancias globales conocidas de Axios
   */
  registerGlobalInterceptors() {
    // Registrar para la instancia global de axios
    this.registerInterceptor(axios, 'global');
    
    // Si hay instancias personalizadas en window, registrarlas también
    if (typeof window !== 'undefined') {
      if (window.nubelyticsAxios) {
        this.registerInterceptor(window.nubelyticsAxios, 'nubelyticsAxios');
      }
      
      if (window.api && window.api.axios) {
        this.registerInterceptor(window.api.axios, 'apiAxios');
      }
    }
    
    console.info('[CorsProxyRegistry] Interceptores globales registrados');

    // Monkey-patch axios.create para que toda nueva instancia lleve interceptor
    if (!axios._corsPatched) {
      const originalCreate = axios.create.bind(axios);
      axios.create = (...args) => {
        const instance = originalCreate(...args);
        // Registrar interceptor automáticamente
        this.registerInterceptor(instance, `auto_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`);
        return instance;
      };
      axios._corsPatched = true;
      console.info('[CorsProxyRegistry] axios.create parcheado para interceptores automáticos');
    }

    // Monkey-patch window.fetch para forzar proxy en dominios problemáticos
    if (typeof window !== 'undefined' && window.fetch && !window.fetch._corsPatched) {
      const originalFetch = window.fetch.bind(window);
      window.fetch = function(resource, init = {}) {
        try {
          let urlStr;
          if (typeof resource === 'string') {
            urlStr = resource;
          } else if (resource && resource.url) {
            urlStr = resource.url;
          }
          if (urlStr) {
            // Evitar doble proxificado: si la URL ya apunta al endpoint del proxy, dejarla intacta
            const proxyEndpoint = corsProxyConfig.getResolvedProxyEndpoint();
            if (proxyEndpoint) {
              const isAlreadyProxied = urlStr.startsWith(proxyEndpoint);
              if (isAlreadyProxied) {
                return originalFetch(resource, init);
              }
            }
            // Resolver absoluta si es relativa
            if (!/^https?:\/\//i.test(urlStr)) {
              const base = init.baseURL || axios.defaults.baseURL || window.location.origin;
              urlStr = new URL(urlStr, base).toString();
            }
            if (corsProxyConfig.shouldAlwaysUseProxy(urlStr)) {
              const proxied = corsProxyConfig.generateProxyUrl(urlStr);
              if (typeof resource === 'string') {
                resource = proxied;
              } else {
                // Antes de clonar verificar que no esté ya proxificado
                if (!(resource instanceof Request && resource.url.startsWith(proxyEndpoint))) {
                  resource = new Request(proxied, resource);
                }
              }
              // Señal diagnostica
              if (init.headers) {
                init.headers['x-proxy-applied'] = 'true';
                init.headers['x-proxy-reason'] = 'forced_domain';
              } else {
                init.headers = {
                  'x-proxy-applied': 'true',
                  'x-proxy-reason': 'forced_domain'
                };
              }
            }
          }
        } catch(e) {
          console.warn('[CorsProxyRegistry] Error al procesar fetch para proxy:', e?.message || e);
        }
        return originalFetch(resource, init);
      };
      window.fetch._corsPatched = true;
      console.info('[CorsProxyRegistry] window.fetch parcheado para proxy automático');
    }
  }
  
  /**
   * Añade un dominio a la lista de dominios que siempre usan proxy
   * @param {string} domain - Dominio a añadir
   * @returns {boolean} true si se añadió correctamente
   */
  addDomain(domain) {
    return addDomainToForceList(domain);
  }
  
  /**
   * Elimina un dominio de la lista de dominios que siempre usan proxy
   * @param {string} domain - Dominio a eliminar
   * @returns {boolean} true si se eliminó correctamente
   */
  removeDomain(domain) {
    return removeDomainFromForceList(domain);
  }
  
  /**
   * Obtiene la lista actual de dominios que siempre usan proxy
   * @returns {Array<string>} Lista de dominios
   */
  getProxyDomains() {
    return corsProxyConfig.ALWAYS_USE_PROXY_DOMAINS;
  }
  
  /**
   * Realiza una prueba de conexión a una URL específica para verificar CORS
   * @param {string} url - URL a probar
   * @returns {Promise<Object>} Resultado de la prueba
   */
  /**
   * Alias backward-compat: runDiagnosticTest (legacy name)
   * @deprecated usa testCorsConnection en adelante
   */
  runDiagnosticTest(url) {
    return this.testCorsConnection(url);
  }

  async testCorsConnection(url) {
    if (!url) {
      return { success: false, error: 'No se proporcionó URL' };
    }
    
    try {
      // Crear una instancia temporal para la prueba
      const testAxios = axios.create();
      const startTime = Date.now();
      
      // Primera prueba sin proxy
      try {
        const directResult = await testAxios.get(url, {
          timeout: 5000,
          headers: { 'x-monitor-check': 'true' }
        });
        
        return {
          success: true,
          useProxy: false,
          status: directResult.status,
          headers: directResult.headers,
          timeMs: Date.now() - startTime
        };
      } catch (directError) {
        // Si falla, probar con proxy
        console.info(`[CorsProxyRegistry] Conexión directa fallida, probando con proxy: ${url}`);
        
        // Registrar el interceptor
        CorsProxyInterceptorSimple.register(testAxios);
        
        try {
          const proxyResult = await testAxios.get(url, {
            timeout: 5000,
            headers: { 'x-monitor-check': 'true' }
          });
          
          return {
            success: true,
            useProxy: true,
            status: proxyResult.status,
            headers: proxyResult.headers,
            timeMs: Date.now() - startTime
          };
        } catch (proxyError) {
          return {
            success: false,
            useProxy: true,
            error: proxyError.message,
            timeMs: Date.now() - startTime
          };
        }
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Crear instancia singleton
const corsProxyRegistry = new CorsProxyRegistry();

// Exportar como singleton
export default corsProxyRegistry;

// Compatibilidad global legacy: exponer en window.EK.corsRegistry
if (typeof window !== 'undefined') {
  window.EK = window.EK || {};
  window.EK.corsRegistry = corsProxyRegistry;
}

// Exportar función de inicialización para uso en main.js o App.js
export const initCorsProxy = () => {
  corsProxyRegistry.registerGlobalInterceptors();
  return corsProxyRegistry;
};
